/**
 * The receipt, at the one moment the till knows which order it is for.
 *
 * `useIssueReceipt` had no caller anywhere, so no receipt was ever produced:
 * a bill was settled, the tab closed, and the fiscal document the guest is
 * owed did not exist. The settled screen is where it belongs — it is the only
 * point at which the order id is in hand, because nothing lists a closed order
 * afterwards.
 *
 * **Asking twice is a reprint, and a reprint says so.** The server returns the
 * same document with the same number and counts the reprint; minting a fresh
 * number would leave a hole in the sequence and two originals for one sale. So
 * a reprint is printed marked COPY — a second piece of paper that could pass as
 * an original is exactly what a fiscal sequence exists to prevent.
 *
 * Rendered from the frozen payload alone, never from the live order or menu, so
 * a receipt printed tomorrow shows what the guest paid today.
 */

import { useState } from 'react';
import {
  displayLocale,
  TENDER_LABELS,
  formatMoney,
  useEmailReceipt,
  useEntitlements,
  useIssueReceipt,
  usePermission,
  useReceiptBranding,
  type Receipt,
  type TenderType,
} from '@restaurant/api-client';
import {
  ActionError,
  Button,
  ErrorNotice,
  Field,
  Input,
  ReceiptPaper,
  Sheet,
  receiptLines,
} from '@restaurant/ui';

import { PrinterError, loadPrinter, sendToPrinter } from './printer';

export function ReceiptButton({
  orderId,
  locationId,
}: {
  orderId: string;
  locationId: string | undefined;
}) {
  const mayIssue = usePermission('fiscal.receipt.issue');
  const issue = useIssueReceipt();
  const [showing, setShowing] = useState<Receipt | null>(null);

  if (!mayIssue) return null;

  return (
    <>
      <Button
        size="lg"
        loading={issue.isPending}
        onClick={() => issue.mutate({ orderId }, { onSuccess: setShowing })}
      >
        Receipt
      </Button>
      <ActionError error={issue.error} action="Producing the receipt" />
      {showing && (
        <ReceiptSheet
          receipt={showing}
          locationId={locationId}
          onDismiss={() => setShowing(null)}
        />
      )}
    </>
  );
}

function ReceiptSheet({
  receipt,
  locationId,
  onDismiss,
}: {
  receipt: Receipt;
  locationId: string | undefined;
  onDismiss: () => void;
}) {
  const copy = receipt.reprint_count > 0;
  const branding = useReceiptBranding(locationId);
  // Not a practice slip: an email is a real one, and practice sends nothing.
  const mayEmail =
    useEntitlements().has('fiscal.branded_receipts') && receipt.payload.practice !== true;
  const printer = loadPrinter();
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);

  // A network printer is sent the receipt as text, laid out by the same reader
  // of the frozen payload as the paper below; any other printer gets the
  // browser's print of that paper.
  async function print() {
    if (printer.kind === 'browser') {
      window.print();
      return;
    }
    setPrinting(true);
    setPrintError(null);
    try {
      await sendToPrinter(printer, {
        lines: receiptLines({
          reference: receipt.reference,
          reprintCount: receipt.reprint_count,
          issuedAt: receipt.issued_at,
          payload: receipt.payload,
          money: (amount) => formatMoney(amount),
          tender: (key) => TENDER_LABELS[key as TenderType] ?? key,
          branding: branding.data,
          locale: displayLocale(),
          width: printer.width,
        }),
        cut: true,
        drawer: false,
      });
    } catch (e) {
      setPrintError(e instanceof PrinterError ? e.message : 'The receipt did not print.');
    } finally {
      setPrinting(false);
    }
  }

  return (
    <Sheet
      title={copy ? `Copy of ${receipt.reference}` : `Receipt ${receipt.reference}`}
      onDismiss={onDismiss}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          loading={printing}
          onClick={() => void print()}
        >
          Print
        </Button>
      }
    >
      {copy && (
        <p className="rcpt__copy-note">
          This order already had a receipt, so this is the same one again — same number, marked
          as a copy. Printed {receipt.reprint_count + 1} times.
        </p>
      )}

      {printError && <ErrorNotice title="The receipt did not print" message={printError} />}

      <ReceiptPaper
        locale={displayLocale()}
        reference={receipt.reference}
        reprintCount={receipt.reprint_count}
        issuedAt={receipt.issued_at}
        payload={receipt.payload}
        money={(amount) => formatMoney(amount)}
        tender={(key) => TENDER_LABELS[key as TenderType] ?? key}
        branding={branding.data}
      />

      {/* Offered only where the plan has it. A guest standing at the counter
          is not the moment to read an upgrade prompt aloud. */}
      {mayEmail && <EmailReceipt receiptId={receipt.id} />}
    </Sheet>
  );
}

/**
 * Sending the receipt to the guest instead of, or as well as, printing it.
 *
 * Typed by whoever is at the till while the guest reads it out, so the address
 * stays on screen after sending: "is that right?" is answered by looking.
 */
function EmailReceipt({ receiptId }: { receiptId: string }) {
  const email = useEmailReceipt();
  const [to, setTo] = useState('');

  return (
    <div className="rc__email">
      <Field label="Email it" hint="Sent now, under the restaurant's name.">
        <Input
          type="email"
          inputMode="email"
          autoComplete="off"
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            email.reset();
          }}
          placeholder="guest@example.com"
        />
      </Field>
      {email.data ? (
        <p className="rc__email-sent" role="status">
          Sent to {email.data.to_address}.
        </p>
      ) : (
        <Button
          size="lg"
          block
          loading={email.isPending}
          disabled={!to.includes('@')}
          onClick={() => email.mutate({ receiptId, to: to.trim() })}
        >
          Send receipt
        </Button>
      )}
      <ActionError error={email.error} action="Emailing a receipt" />
    </div>
  );
}
